import { ChoosePeriodModal } from "@/components/date-filter/ChoosePeriodModal.tsx";
import { dateToPartialDate } from "@/components/date-filter/date-filter-presets.ts";
import {
  ApiDateFilter,
  ApiDateFilterMode,
  ApiDateFilterOperator,
} from "@/components/date-filter/date-filter.type.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { Avatar, Box, ButtonIcon, FontAwesomeIcon, Text, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface DateRangeFilterValue {
  from?: Date;
  to?: Date;
}

export interface DateRangeFilterProps {
  title: string;
  from: Date | undefined;
  to: Date | undefined;
  onChange: (value: DateRangeFilterValue) => void;
}

export function DateRangeFilter({ title, from, to, onChange }: DateRangeFilterProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderDate, buildFromPartialDate } = useFormatter();

  const hasValue = Boolean(from || to);

  const display =
    from && to
      ? `${renderDate(from)} – ${renderDate(to)}`
      : from
        ? `${t("common:date_from_short")} ${renderDate(from)}`
        : to
          ? `${t("common:date_before")} ${renderDate(to)}`
          : "";

  const openCustom = () => {
    const value: ApiDateFilter = {
      mode: ApiDateFilterMode.date,
      operator: ApiDateFilterOperator.custom,
      value_from: from ? dateToPartialDate(from, ApiDateFilterMode.date) : undefined,
      value_to: to ? dateToPartialDate(to, ApiDateFilterMode.date) : undefined,
      preset: undefined,
    };

    openModal(
      ChoosePeriodModal,
      {
        value,
        availableModes: [ApiDateFilterMode.date],
        onSave: (next: ApiDateFilter | undefined) =>
          onChange({
            from: buildFromPartialDate(next?.value_from ?? undefined),
            to: buildFromPartialDate(next?.value_to ?? undefined),
          }),
      },
      { size: "md" }
    );
  };

  return (
    <Box
      onClick={openCustom}
      className={
        "flex h-14 cursor-pointer items-center justify-between gap-sm border-b border-dark/10"
      }
    >
      <Box className={"flex min-w-0 flex-col justify-center"}>
        {hasValue ? (
          <>
            <Text size={"sm"} className={"text-secondary leading-tight"}>
              {title}
            </Text>
            <Text className={"truncate font-semibold text-dark leading-tight"}>{display}</Text>
          </>
        ) : (
          <Text className={"text-secondary"}>{title}</Text>
        )}
      </Box>
      <Box className={"flex shrink-0 items-center"}>
        {hasValue ? (
          <ButtonIcon
            variant={"plain"}
            icon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
            onClick={(e) => {
              e.stopPropagation();
              onChange({});
            }}
          />
        ) : (
          <Avatar
            variant={"plain"}
            icon={<FontAwesomeIcon className={"text-dark/70"} icon={"fa-light fa-chevron-down"} />}
          />
        )}
      </Box>
    </Box>
  );
}
